<!--
Fork addition: top balance bar — the plan's net exchange with the
outside world at solved rates, always visible under the toolbar.
Left: NEEDS IN (products the plan consumes more of than it makes —
they must be supplied from outside). Right: SURPLUS OUT (products
made but not consumed inside the plan). Derived state only, from
scripts/flow-status.ts; pseudo-products (workers, electricity, ...)
are excluded there.
-->
<script setup lang="ts">
import {computed} from 'vue';
import {injectBlueprintModel} from '@/scripts/model/store';
import {calculateNetBalance, type NetBalanceEntry} from '@/scripts/flow-status';
import {formatItem} from '@/scripts/format';

const blueprintModel = injectBlueprintModel();

const itemCount = computed(() => {
    void blueprintModel.itemsGenerationNumber;
    return [...blueprintModel.items].length;
});
const balance = computed(() => {
    //depend on the generation counters so graph edits and re-solves
    //refresh the bar like they refresh the other panels
    void blueprintModel.itemsGenerationNumber;
    void blueprintModel.summaryGenerationNumber;
    return calculateNetBalance(blueprintModel);
});

function entryTitle(entry: NetBalanceEntry, kind: 'in' | 'out'): string {
    return (kind === 'in')
        ? `${entry.item.label}: the plan consumes ${formatItem(entry.rate, entry.item)}/min more than it produces — supply it from outside`
        : `${entry.item.label}: the plan produces ${formatItem(entry.rate, entry.item)}/min more than it consumes`;
}
</script>

<template>
    <div v-if="itemCount && balance.hasSolved" class="balance-bar text-caption">
        <template v-if="balance.needsIn.length || balance.surplusOut.length">
            <div class="balance-group balance-group-in">
                <template v-if="balance.needsIn.length">
                    <span class="balance-label balance-label-in">NEEDS IN</span>
                    <span
                        v-for="entry in balance.needsIn"
                        :key="`in-${entry.item.name}`"
                        class="balance-entry"
                        :title="entryTitle(entry, 'in')"
                    >
                        <span class="balance-icon"><icon-component :image="entry.item.image" /></span>
                        <span class="balance-entry-text">{{ entry.item.label }} {{ formatItem(entry.rate, entry.item) }}</span>
                    </span>
                </template>
            </div>
            <div class="balance-group balance-group-out">
                <template v-if="balance.surplusOut.length">
                    <span class="balance-label balance-label-out">SURPLUS OUT</span>
                    <span
                        v-for="entry in balance.surplusOut"
                        :key="`out-${entry.item.name}`"
                        class="balance-entry"
                        :title="entryTitle(entry, 'out')"
                    >
                        <span class="balance-icon"><icon-component :image="entry.item.image" /></span>
                        <span class="balance-entry-text">{{ entry.item.label }} {{ formatItem(entry.rate, entry.item) }}</span>
                    </span>
                </template>
            </div>
        </template>
        <div v-else class="balance-balanced">
            Balanced — no external inputs, no surplus
        </div>
    </div>
</template>

<style scoped>
.balance-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    min-height: 26px;
    padding: 1px 10px;
    box-sizing: border-box;
    border-bottom: 1px solid rgba(128, 128, 128, 0.35);
    /* theme-neutral surface tint, readable in light and dark mode */
    background: rgba(128, 128, 128, 0.08);
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: none;
}
.balance-bar::-webkit-scrollbar {
    display: none;
}
.balance-group {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
}
.balance-group-out {
    margin-left: auto;
}
.balance-label {
    font-weight: 700;
    letter-spacing: 0.06em;
    white-space: nowrap;
}
.balance-label-in {
    color: #e65100;
}
.balance-label-out {
    color: #2e7d32;
}
.v-theme--dark .balance-label-in {
    color: #ffb74d;
}
.v-theme--dark .balance-label-out {
    color: #81c784;
}
.balance-entry {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    white-space: nowrap;
}
/* icons render at settings.iconSize (32px) — shrink into the slim bar */
.balance-icon {
    display: inline-block;
    width: 16px;
    height: 16px;
    overflow: hidden;
    flex: 0 0 auto;
}
.balance-icon > * {
    transform: scale(0.5);
    transform-origin: top left;
}
.balance-entry-text {
    font-weight: 600;
}
.balance-balanced {
    width: 100%;
    text-align: center;
    opacity: 0.6;
}
/* fork addition (Stage I): phone — smaller type and tighter spacing,
   and both groups keep their full content width so the bar scrolls
   sideways as one slim row instead of wrapping into a tall stack or
   clipping entries */
@media (max-width: 900px) {
    .balance-bar {
        gap: 8px;
        padding: 1px 6px;
        min-height: 24px;
    }
    .balance-group {
        gap: 6px;
        min-width: max-content;
    }
    .balance-label, .balance-entry-text {
        font-size: 11px;
    }
}
</style>
